import { useState } from 'react';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import type { TimetableScheduleBlock } from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import { RemoveScheduleResult } from 'src/utils/timetables/reassignToDefault';

export interface UseRemoveFromTimetablePromptArgs<T> {
  buildBlocks: (target: T) => RemoveScheduleResult | undefined;
  submit: (blocks: TimetableScheduleBlock[]) => Promise<void>;
  getErrorMessage: (target: T) => string;
}

export const useRemoveFromTimetablePrompt = <T>({
  buildBlocks,
  submit,
  getErrorMessage,
}: UseRemoveFromTimetablePromptArgs<T>) => {
  // Kept after closing so the prompt's copy doesn't blank out mid-animation.
  const [target, setTarget] = useState<T | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const open = (nextTarget: T) => {
    setTarget(nextTarget);
    setIsOpen(true);
  };

  const close = () => {
    if (!isSubmitting) setIsOpen(false);
  };

  const confirm = async () => {
    if (target === null) return;
    const result = buildBlocks(target);
    if (!result) return;

    const errorMessage = getErrorMessage(target);
    if (result.success === false) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: errorMessage });
      return;
    }

    setIsSubmitting(true);
    try {
      await submit(result.blocks);
      setIsOpen(false);
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { target, isOpen, isSubmitting, open, close, confirm };
};
